import React, {useEffect, useMemo, useState} from 'react';
import AgencySalesByChildAgenciesProperties from "./AgencySalesByChildAgenciesProperties";
import {useStyles} from "./AgencySalesByChildAgencies.style";
import AgencySalesByChildAgenciesStore from "./AgencySalesByChildAgenciesStore";
import {useCommonContext} from "../../../contexts/CommonContext";
import {Observer} from "mobx-react-lite";
import {useI18nContext} from "../../../contexts/I18nContext";
import PriceLockup from "../../../ui_components/PriceLockup/PriceLockup";
import ShowSellerBasicInfo from "../../Info/ShowSellerBasicInfo/ShowSellerBasicInfo";
import InfoIcon from "@mui/icons-material/Info";
import PageLoading from "../../../ui_components/PageLoading/PageLoading";
import clsx from "clsx";
import {useUserContext} from "../../../contexts/UserContext";
import Tag from "../../../ui_components/Tag/Tag";
import TagType from "../../../ui_components/Tag/TagType";
import SaleByServiceResponse from "../../../model/sales/SaleByServiceResponse";
import ServiceName from "../../../entities/ServiceName";
import MoneyDeliveryOverview from "../MoneyDeliveryOverview/MoneyDeliveryOverview";
import SaleByAgencyResponse from "../../../model/sales/SaleByAgencyResponse";
import DownloadAgencyOrdersReportButton
    from "../../Buttons/DownloadAgencyOrdersReportButton/DownloadAgencyOrdersReportButton";
import ButtonType from "../../../ui_components/Button/ButtonType";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import ExpandoMenu from "../../../ui_components/ExpandoMenu/ExpandoMenu";
import DownloadSummarizedAgencyOrdersReportButton from '../../Buttons/DownloadSummarizedAgencyOrdersReportButton/DownloadSummarizedAgencyOrdersReportButton';
import FilterInput from "../../Inputs/FilterInput/FilterInput";

const AgencySalesByChildAgencies: React.FC<AgencySalesByChildAgenciesProperties> = ({
                                                                                        from,
                                                                                        to,
                                                                                        agencyId,
                                                                                        title,
                                                                                        className = '',
                                                                                    }) => {

    const i18n = useI18nContext();
    const commonStore = useCommonContext();
    const userStore = useUserContext();
    const store = useMemo(() => new AgencySalesByChildAgenciesStore(commonStore, from, to, agencyId),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [commonStore, from, to, agencyId]);
    const classes = useStyles();

    const [filter, setFilter] = useState('');

    useEffect(() => {
        store.getElements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to]);

    const RenderPriceLockup: React.FC<{
        agencySale: SaleByAgencyResponse,
        service: SaleByServiceResponse
    }> = ({agencySale, service}) => {
        const getThemeColor = (serviceName: ServiceName) => {
            switch (serviceName) {
                case ServiceName.MONEY_DELIVERY:
                    return 'purple';
                case ServiceName.MISCELLANY:
                    return 'info';
                case ServiceName.ARTICLE_DELIVERY:
                    return 'primary';
                case ServiceName.PACKAGE_DELIVERY:
                    return 'secondary';
                case ServiceName.TOPUP:
                    return 'warning';
                case ServiceName.SHOP:
                    return 'success';
                default:
                    return 'purple';
            }
        };

        switch (service.serviceName) {
            case ServiceName.MONEY_DELIVERY:
                return <PriceLockup
                    value={service.total}
                    title={i18n.translate(service.serviceName)}
                    total={agencySale.data.breakdown.reduce((t, c) => t + c.total, 0)}
                    size={15}
                    orders={service.amount}
                    theme={getThemeColor(service.serviceName)}>
                    <MoneyDeliveryOverview
                        from={from}
                        to={to}
                        agencyId={agencySale.agencyId}
                        size="small"
                    />
                </PriceLockup>
            default:
                return <PriceLockup
                    value={service.total}
                    title={i18n.translate(service.serviceName)}
                    total={agencySale.data.breakdown.reduce((t, c) => t + c.total, 0)}
                    size={15}
                    orders={service.amount}
                    theme={getThemeColor(service.serviceName)}/>
        }
    }

    return <Observer>{() => {
        const filteredBreakdown = (store.sales?.breakdown || []).filter(d =>
            d.agencyName.toLowerCase().includes(filter.toLowerCase())
        );
        return (
        <div className={classes.container}>
            <div className={classes.title}>{title}</div>
            <FilterInput
                value={filter}
                placeholder={'Filtrar por agencia'}
                onChange={setFilter}
                fullWidth
                className={classes.filter_container}
            />
            <div className={classes.item_row_container}>
                {store.sales && filteredBreakdown.map((d, i) => (
                    <div key={i}
                         className={clsx(classes.item_row, d.agencyId === userStore.currentUser()?.agencyId && classes.current_row)}
                    >
                        <div className={classes.agency_container}>
                            {d.agencyId === userStore.currentUser()?.agencyId &&
                                <Tag className={classes.current_agency} text={"Mi Agencia"}
                                     tagType={TagType.SECONDARY}/>
                            }
                            <ShowSellerBasicInfo autofill name={d.agencyName} email={d.agencyEmail}
                                                 phone={d.agencyPhone}/>

                        </div>
                        <PriceLockup value={d.data.breakdown.reduce((t, c) => t + c.total, 0)}
                                     size={20}
                                     theme={"primary"}
                                     withBackground
                                     orders={d.data.breakdown.reduce((t, c) => t + c.amount, 0)}
                                     title={"Total de ventas"}/>
                        <div className={classes.item_sales_container}>
                            {d.data.breakdown.map((dd, ii) => (
                                <RenderPriceLockup service={dd} agencySale={d} key={ii}/>
                            ))}
                        </div>
                        <ExpandoMenu>
                            <>
                                <DownloadSummarizedAgencyOrdersReportButton
                                    type={ButtonType.SECONDARY}
                                    showPreview={true}
                                    agencyId={d.agencyId}
                                    from={from}
                                    to={to}
                                    size={ButtonSize.SMALL}
                                />
                                <DownloadAgencyOrdersReportButton
                                    type={ButtonType.SECONDARY}
                                    showPreview={true}
                                    agencyId={d.agencyId}
                                    from={from}
                                    to={to}
                                    size={ButtonSize.SMALL}
                                />
                            </>
                        </ExpandoMenu>
                    </div>
                ))}
            </div>
            {store.sales && filteredBreakdown.length === 0 &&
                <div className={classes.no_content_container} onClick={() => store.getElements()}>
                    <InfoIcon/>
                    {store.sales.breakdown.length === 0 ? i18n.translate("NO_SALES") : 'Sin agencias que coincidan con el filtro'}
                </div>
            }
            <PageLoading className={classes.loading_container} loadingSize={40} isLoading={store.loading}
                         text={`${i18n.translate("LOADING")} ${title || ''}`}/>
        </div>
        );
    }}</Observer>

}


export default AgencySalesByChildAgencies;